import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { STUFF_DETAIL } from "@/routes";
import { extractEntityLocation } from "@/utils/locationUtils";
import type { Event } from "@/services/events.service";

const escapeICS = (value: string) =>
  value.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");

/** Safely extract a string from a value that may be an object (e.g. { text: "..." }). */
const safeStr = (v: unknown): string | null => {
  if (!v) return null;
  if (typeof v === 'string') return v.trim() || null;
  if (typeof v === 'object' && 'text' in (v as Record<string, unknown>)) {
    const text = (v as { text?: unknown }).text;
    return typeof text === 'string' ? text.trim() || null : null;
  }
  return null;
};

export interface CalendarEventData {
  // Event basic info
  name?: string;
  description?: string;
  location?: string;
  url?: string;
  entityId?: string;

  // Dates
  dtstart?: string;
  dtend?: string;
  allDay?: boolean;

  // Linked venue
  linkedVenue?: {
    address?: string;
    location?: string;
    name?: string;
  };

  // Source entity data (for accessing nested fields)
  sourceEntity?: any;
}

/**
 * Normalize event data from various sources to a consistent structure
 */
export const normalizeEventData = (
  data: CalendarEventData,
): {
  title: string;
  description: string;
  location: string;
  url: string;
  dtstart: string | null;
  dtend: string | null;
  allDay: boolean;
} => {
  // Extract title - try multiple paths
  const title =
    data.name ||
    data.sourceEntity?.name ||
    data.sourceEntity?.entity_name ||
    data.sourceEntity?.entity_data?.name ||
    data.sourceEntity?.link_data?.entity_name ||
    data.sourceEntity?.link_data?.calendar_summary ||
    data.sourceEntity?.entity_data?.calendar_data?.calendar_summary ||
    "Stasht event";

  let description =
    safeStr(data.description) ||
    safeStr(data.sourceEntity?.description) ||
    safeStr(data.sourceEntity?.details) ||
    safeStr(data.sourceEntity?.entity_data?.details) ||
    safeStr(data.sourceEntity?.entity_data?.description) ||
    "";

  // Extract location — use the same logic as EntityHeader
  const location = extractEntityLocation(data.sourceEntity) || safeStr(data.location) || "";

  // Extract entity ID for Stasht URL
  const entityId = data.entityId || data.sourceEntity?.entity_id || data.sourceEntity?.id || "";

  // Build Stasht URL
  const stashtUrl = entityId ? `https://stasht.app${STUFF_DETAIL({ entityId })}` : "";

  // Add "Open in Stasht" link to description
  if (stashtUrl) {
    if (description) {
      description = `${description}\n\nOpen in Stasht: ${stashtUrl}`;
    } else {
      description = `Open in Stasht: ${stashtUrl}`;
    }
  }

  // Extract URL - use entity URL if available
  const url = stashtUrl || data.url || "";

  // Extract dates - prioritize UTC dates from calendar_data
  let dtstart: string | null = null;
  let dtend: string | null = null;
  let allDay = false;

  // Try multiple paths for dtstart (prioritize UTC)
  dtstart =
    data.dtstart ||
    data.sourceEntity?.entity_data?.calendar_data?.dtstart_utc ||
    data.sourceEntity?.entity_data?.calendar_data?.dtstart ||
    data.sourceEntity?.link_data?.dtstart ||
    data.sourceEntity?.calendar_data?.dtstart_utc ||
    data.sourceEntity?.calendar_data?.dtstart ||
    null;

  // Try multiple paths for dtend (prioritize UTC)
  dtend =
    data.dtend ||
    data.sourceEntity?.entity_data?.calendar_data?.dtend_utc ||
    data.sourceEntity?.entity_data?.calendar_data?.dtend ||
    data.sourceEntity?.link_data?.dtend ||
    data.sourceEntity?.calendar_data?.dtend_utc ||
    data.sourceEntity?.calendar_data?.dtend ||
    data.sourceEntity?.end_date ||
    data.sourceEntity?.endDate ||
    data.sourceEntity?.dend ||
    null;

  // Check if all-day event
  if (data.allDay !== undefined) {
    allDay = data.allDay;
  } else if (data.sourceEntity?.entity_data?.calendar_data?.all_day !== undefined) {
    allDay = data.sourceEntity.entity_data.calendar_data.all_day;
  } else if (data.sourceEntity?.calendar_data?.all_day !== undefined) {
    allDay = data.sourceEntity.calendar_data.all_day;
  } else if (dtstart) {
    // Check if it's all day: no time component OR time is exactly 00:00:00
    const hasTimeComponent = dtstart.includes("T");
    const isMidnight = dtstart.match(/T00:00:00(?:\.000)?Z?$/i);
    allDay = !hasTimeComponent || Boolean(isMidnight);
  }

  return {
    title,
    description,
    location,
    url,
    dtstart,
    dtend,
    allDay,
  };
};

/**
 * Format date for Google Calendar (YYYYMMDDTHHmmssZ)
 */
const formatGoogleCalendarDate = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");
  return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
};

/**
 * Format date for Outlook (YYYY-MM-DDTHH:mm:ssZ)
 */
const formatOutlookDate = (date: Date): string => {
  return date
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
};

/**
 * Generate Google Calendar create-event link
 */
export const generateGoogleCalendarLink = (data: CalendarEventData): string | null => {
  const normalized = normalizeEventData(data);

  if (!normalized.dtstart) {
    return null;
  }

  const startDate = new Date(normalized.dtstart);
  if (isNaN(startDate.getTime())) {
    return null;
  }

  // Default to 2 hours if no end date
  let endDate: Date;
  if (normalized.dtend) {
    endDate = new Date(normalized.dtend);
    if (isNaN(endDate.getTime())) {
      endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
    }
  } else {
    endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
  }

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: normalized.title,
    dates: `${formatGoogleCalendarDate(startDate)}/${formatGoogleCalendarDate(endDate)}`,
  });

  if (normalized.location) {
    params.append("location", normalized.location);
  }

  if (normalized.description) {
    params.append("details", normalized.description);
  }

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Generate Outlook.com / Office 365 compose link
 */
export const generateOutlookCalendarLink = (data: CalendarEventData): string | null => {
  const normalized = normalizeEventData(data);

  if (!normalized.dtstart) {
    return null;
  }

  const startDate = new Date(normalized.dtstart);
  if (isNaN(startDate.getTime())) {
    return null;
  }

  // Default to 2 hours if no end date
  let endDate: Date;
  if (normalized.dtend) {
    endDate = new Date(normalized.dtend);
    if (isNaN(endDate.getTime())) {
      endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
    }
  } else {
    endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
  }

  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: normalized.title,
    startdt: formatOutlookDate(startDate),
    enddt: formatOutlookDate(endDate),
  });

  if (normalized.location) {
    params.append("location", normalized.location);
  }

  if (normalized.description) {
    params.append("body", normalized.description);
  }

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

/**
 * Generate an ICS calendar file content for an event
 */
export const generateCalendarICS = (data: CalendarEventData): string | null => {
  const normalized = normalizeEventData(data);

  if (!normalized.dtstart) {
    return null; // Can't create calendar event without start date
  }

  const startDate = new Date(normalized.dtstart);
  if (isNaN(startDate.getTime())) {
    return null;
  }

  // Default to 2 hours if no end date
  let endDate: Date;
  if (normalized.dtend) {
    endDate = new Date(normalized.dtend);
    if (isNaN(endDate.getTime())) {
      endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
    }
  } else {
    endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
  }

  // Generate UID for the event (stable per event)
  const entityId = data.entityId || data.sourceEntity?.entity_id || data.sourceEntity?.id || "";
  const uid = entityId ? `${entityId}@stasht.app` : `${Date.now()}@stasht.app`;

  // Format dates based on all_day flag
  const formatDate = (date: Date, isAllDay: boolean): string => {
    if (isAllDay) {
      // Use UTC components directly — date-fns format() uses local time,
      // which shifts all-day dates back one day in negative-UTC-offset timezones
      const y = date.getUTCFullYear();
      const m = String(date.getUTCMonth() + 1).padStart(2, "0");
      const d = String(date.getUTCDate()).padStart(2, "0");
      return `${y}${m}${d}`;
    } else {
      // Format as DATETIME: YYYYMMDDTHHmmssZ (UTC)
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      const hours = String(date.getUTCHours()).padStart(2, "0");
      const minutes = String(date.getUTCMinutes()).padStart(2, "0");
      const seconds = String(date.getUTCSeconds()).padStart(2, "0");
      return `${year}${month}${day}T${hours}${minutes}${seconds}Z`;
    }
  };

  const dtstartValue = normalized.allDay
    ? `DTSTART;VALUE=DATE:${formatDate(startDate, true)}`
    : `DTSTART:${formatDate(startDate, false)}`;

  const dtendValue = normalized.allDay
    ? `DTEND;VALUE=DATE:${formatDate(endDate, true)}`
    : `DTEND:${formatDate(endDate, false)}`;

  // Generate DTSTAMP (current time in UTC)
  const dtstamp = formatDate(new Date(), false);

  const icsLines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//stasht.app//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    dtstartValue,
    dtendValue,
    `SUMMARY:${escapeICS(normalized.title)}`,
    normalized.description ? `DESCRIPTION:${escapeICS(normalized.description)}` : undefined,
    normalized.location ? `LOCATION:${escapeICS(normalized.location)}` : undefined,
    normalized.url ? `URL:${escapeICS(normalized.url)}` : undefined,
    `LAST-MODIFIED:${dtstamp}`,
    "SEQUENCE:0",
    "STATUS:CONFIRMED",
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean) as string[];

  return icsLines.join("\r\n");
};

/**
 * Generate a safe filename from event title
 */
export const generateCalendarFilename = (title: string): string => {
  const sanitized = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${sanitized || "stasht-event"}.ics`;
};

/**
 * Download a calendar ICS file
 * Works on both web and native mobile platforms
 */
export const downloadCalendarFile = async (icsContent: string, filename: string = "stasht-event.ics") => {
  if (Capacitor.isNativePlatform()) {
    try {
      // Write .ics to cache, then share the file URI.
      // On iOS the share sheet shows "Add to Calendar" for .ics files.
      const result = await Filesystem.writeFile({
        path: filename,
        data: icsContent,
        directory: Directory.Cache,
        encoding: Encoding.UTF8,
      });

      await Share.share({ url: result.uri });
    } catch (error: any) {
      // User cancelled share — not an error
      if (error?.message?.includes("cancelled") || error?.message?.includes("cancel")) return;
      console.error("Error sharing calendar file:", error);
      throw error;
    }
  } else {
    // Web — standard blob download, OS opens .ics in default calendar app
    const blob = new Blob([icsContent], { type: "text/calendar; charset=utf-8" });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  }
};
